import React, { useContext, useState, useEffect } from 'react';
import api from 'api';
import { FeatureFlagsContext } from 'components/shared/feature-flags';

import HeroLearn from 'containers/user/learn/components/hero-learn/hero-learn-partial';
import CategoriesContent from 'containers/user/learn/components/categories-content/categories-content';
import Loader from 'components/partials/loader/loader';
import NothingFound from 'containers/user/learn/components/empty-row/empty-row';
import CoursesCards from 'containers/user/learn/components/courses-cards/courses-cards';
import useFetchCourses from 'containers/user/learn/containers/content/cources/use-fetch-courses';

import { useCurrentUser } from 'hooks/api/use-current-user';

interface FeatureFlagsContextValue {
  featureFlags: Record<string, boolean>;
}

interface ContentItem {
  name?: string;
  route: string;
  coverImage?: string;
  coverDescription?: string;
  publishedAt?: string;
  releaseNotesDate?: string;
  [key: string]: unknown;
}

interface ContentTypeConfig {
  apiMethod: (userId: string) => Promise<unknown>;
  setState: React.Dispatch<React.SetStateAction<ContentItem[]>>;
  path: string;
  extraAttributes?: string[];
  sortField?: string;
}

interface ApiResponseEntry {
  id: string | number;
  attributes?: {
    name?: string;
    coverImage?: { data?: { attributes?: { url?: string } } };
    [key: string]: unknown;
  };
}

const Learn = (): React.JSX.Element => {
  const { featureFlags } = useContext(
    FeatureFlagsContext
  ) as FeatureFlagsContextValue;
  const [loading, setLoading] = useState<boolean>(false);
  const user = useCurrentUser();

  const fetchCoursesResult = useFetchCourses(user?.data?.oktaUserId);
  const courses = fetchCoursesResult.courses;

  const [guides, setGuides] = useState<ContentItem[]>([]);
  const [articles, setArticles] = useState<ContentItem[]>([]);
  const [webinars, setWebinars] = useState<ContentItem[]>([]);
  const [downloads, setDownloads] = useState<ContentItem[]>([]);
  const [releaseNotes, setReleaseNotes] = useState<ContentItem[]>([]);

  const contentTypes: Record<string, ContentTypeConfig> = {
    guides: {
      apiMethod: api.getGuides,
      setState: setGuides,
      path: 'guides',
      extraAttributes: ['coverDescription', 'publishedAt'],
      sortField: 'publishedAt',
    },
    articles: {
      apiMethod: api.getArticles,
      setState: setArticles,
      path: 'articles',
    },
    webinars: {
      apiMethod: api.getWebinars,
      setState: setWebinars,
      path: 'webinars',
      extraAttributes: ['coverDescription'],
    },
    downloads: {
      apiMethod: api.getDownloads,
      setState: setDownloads,
      path: 'downloads',
      extraAttributes: ['coverDescription'],
    },
    releaseNotes: {
      apiMethod: api.getReleaseNotes,
      setState: setReleaseNotes,
      path: 'product-announcements',
      extraAttributes: ['coverDescription', 'releaseNotesDate'],
      sortField: 'releaseNotesDate',
    },
  };

  useEffect(() => {
    const fetchData = async ({
      apiMethod,
      setState,
      path,
      extraAttributes = [],
      sortField,
    }: ContentTypeConfig): Promise<void> => {
      try {
        const res = (await apiMethod(user?.data?.oktaUserId ?? '')) as {
          data?: { data?: ApiResponseEntry[] };
        };
        let data = res?.data?.data?.map((el) => {
          const baseData: ContentItem = {
            name: el.attributes?.name,
            route: `/learn/content/${path}/${el.id}`,
            coverImage: el.attributes?.coverImage?.data?.attributes?.url,
          };

          extraAttributes.forEach((attr) => {
            baseData[attr] = el.attributes?.[attr];
          });

          return baseData;
        });

        if (sortField && data) {
          data = data.sort((a, b) => {
            const dateA = a[sortField]
              ? new Date(a[sortField] as string)
              : new Date(0);
            const dateB = b[sortField]
              ? new Date(b[sortField] as string)
              : new Date(0);

            return dateB.getTime() - dateA.getTime();
          });
        }

        setState(data?.slice(0, 3) || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    setLoading(true);

    const fetchPromises = Object.entries(featureFlags).reduce<Promise<void>[]>(
      (acc, [key, isEnabled]) => {
        if (isEnabled && contentTypes[key]) {
          acc.push(fetchData(contentTypes[key]));
        }

        return acc;
      },
      []
    );

    Promise.all(fetchPromises).finally(() => setLoading(false));
  }, [featureFlags, user?.data?.oktaUserId]);

  const isAllEmpty = [
    guides,
    articles,
    webinars,
    downloads,
    releaseNotes,
    courses,
  ].every((array) => array.length === 0);

  return (
    <div className="w-full">
      <Loader loading={loading} />
      <HeroLearn />
      <div className="flex flex-col items-center">
        {isAllEmpty ? (
          <NothingFound
            customClass="mt-10"
            customTitle="There is no data yet"
          />
        ) : (
          <>
            {!!articles.length && (
              <CategoriesContent
                title="Articles"
                subtitle="Expert Insights on Trending Topics"
                items={articles}
                categoryLink="/learn/content/articles"
              />
            )}
            {!!courses.length && (
              <CoursesCards
                items={courses.slice(0, 2)}
                userId={user?.data?.oktaUserId}
                title="Available courses"
                subtitle="Learn How To Make the Most of MARKETview"
                showSeeAllButton
              />
            )}
            {!!downloads.length && (
              <CategoriesContent
                title="Downloads"
                subtitle="View Infographics, Whitepapers, Case Studies, and More"
                items={downloads}
                categoryLink="/learn/content/downloads"
              />
            )}
            {!!webinars.length && (
              <CategoriesContent
                title="Webinars"
                subtitle="Watch Recordings From Recent Webinars"
                items={webinars}
                categoryLink="/learn/content/webinars"
              />
            )}

            {!!guides.length && (
              <CategoriesContent
                title="Guides"
                subtitle="Browse Tutorials and Tips From MARKETview Partners"
                items={guides}
                categoryLink="/learn/content/guides"
              />
            )}
            {!!releaseNotes.length && (
              <CategoriesContent
                title="Product Announcements"
                subtitle="See the latest MARKETview Product Updates"
                items={releaseNotes}
                categoryLink="/learn/content/product-announcements"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Learn;
